import { useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  Stack,
  Checkbox,
  Typography,
  Divider,
  Skeleton,
} from "@mui/material";
import {
  TodoCancelDialog,
} from './';
import {
  CompanyAvatar,
} from '../../sections/company';
import {
  CrewAvatar,
} from '../../sections/profile';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// utils
import { fKrDateDayKST, fKrDateAbsoulte } from '../../utils/formatTime';
import axios from '../../utils/axios';
import { mutate } from "swr";


export default function DoneList({companyId, dones, isActive, isValidating}) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');

  const [isOpenCancelTodo, setIsOpenCancelTodo] = useState(false);
  const [todo, setSelectedTodo] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  let groupDones = new Map();
  dones.forEach((done) => {
    let key = fKrDateDayKST(done.done_at);
    groupDones.has(key)
    ? groupDones.get(key).push(done)
    : groupDones.set(key, [done])
  });

  const openCancelTodo = async (todo) => {
    if(!isActive) return ;
    if(isSubmitting) return ;
    setSelectedTodo(todo);
    setIsOpenCancelTodo(true);
  }

  const closeCancelTodo = async () => {
    setIsOpenCancelTodo(false);
  }

  const clearSelection = async () => {
    setSelectedTodo({});
  }

  const cancelTodo = async () => {
    try {
      setIsSubmitting(true);
      const response = await axios.post(
        `/api/works/todo/crew/company/${companyId}/${todo.id}/cancel/`,
      );
      await mutate(`/api/works/todo/crew/company/${companyId}/`);
      mutate(`/api/works/todo/crew/company/${companyId}/history/`);
      enqueueSnackbar('투두를 완료 취소하였습니다.');
      closeCancelTodo();
      clearSelection();
      setIsSubmitting(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('처리중 에러가 발생하였습니다. 관리자에 문의해 주세요.', { variant: 'error' });
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Typography variant="h6" sx={{ px: 3, pt: 2.5 }}>
        완료 데일리 투두
      </Typography>
      {isValidating && groupDones?.size === 0 && (
        <Stack sx={{ px : 3, my: 2, py: 1 }} spacing={3}>
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Stack>
      )}
      {/* e[0] : key, e[1] : value (done list) */}
      {!isValidating && groupDones?.size === 0 && (
        <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
          아직 완료한 투두가 없어요. 투두를 완료해주세요.
        </Typography>
      )}
      {[...groupDones].map((e, eindex) =>  (
        <Box key={eindex}>
          <Typography
            key={`e-${eindex}`}
            variant="subtitle1"
            sx={{ color: "text.disabled", px: 4, pt: 2 }}
          >
            {e[0]}
          </Typography>

          {/* pc */}
          {mdUp && e[1].map((done, index) => (
            <Stack
              key={`d-${index}`} direction="row" alignItems="center" justifyContent="space-between" spacing={5}
              sx={{
                my: 2, py: 1, px: 3,
                ':hover': {
                  backgroundColor: 'background.neutral',
                },
              }}
            >
              <Stack direction="row" alignItems="center" sx={{ width: 1 }}>
                <Checkbox
                  sx={{ mr: {xs: 2.5, sm: 3} }}
                  checked={true}
                  onClick={() => openCancelTodo(done)}
                  disabled={!isActive}
                />
                <Box sx={{ p: 0, m: 0 }}>
                <Stack>
                  <Stack display="inline-block">
                    <Typography variant="subtitle1" sx={{ textDecoration: "line-through" }}>
                      {done.content}
                    </Typography>
                  </Stack>
                </Stack>
                </Box>
              </Stack>
              <Typography sx={{ color: "text.secondary", textAlign: 'center', maxWidth: '180px', minWidth: '180px' }}>
                {fKrDateAbsoulte(done.target_date)}{done.target_date ? '까지' : ''}
              </Typography>
              {done.created_crew && (
                <CrewAvatar hashId={user?.crew?.randomhash} nickname={user?.crew?.nickname} sx={{ maxWidth: '200px', minWidth: '200px' }} />
              )}
              {done.created_company && (
                <CompanyAvatar companyId={companyId} sx={{ maxWidth: '200px', minWidth: '200px' }} />
              )}
            </Stack>
          ))}

          {/* mo */}
          {!mdUp && e[1].map((done, index) => (
            <Stack
              key={index} direction="row" alignItems="center" spacing={1}
              sx={{
                px: 2, py: 2,
                ':active': {
                  backgroundColor: 'background.neutral',
                },
              }}
            >
              <Checkbox
                checked={true}
                onClick={() => openCancelTodo(done)}
                disabled={!isActive}
              />
              <Stack spacing={.5}>
                <Box sx={{ p: 0, m: 0 }}>
                  <Stack display="inline-block">
                    <Typography variant="subtitle1" sx={{ textDecoration: "line-through" }}>
                      {done.content}
                    </Typography>
                  </Stack>
                </Box>
                {done.created_crew && (
                  <CrewAvatar hashId={user?.crew?.randomhash} width={20} height={20} nameVariant="caption" nickname={user?.crew?.nickname} sx={{ maxWidth: '70vw' }} />
                )}
                {done.created_company && (
                  <CompanyAvatar companyId={companyId} width={20} height={20} nameVariant="caption" sx={{ maxWidth: '70vw' }} />
                )}
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {fKrDateAbsoulte(done.target_date)}{done.target_date ? '까지' : ''}
                </Typography>
              </Stack>
            </Stack>
          ))}
          <Divider key={`ed-${eindex}`} />
        </Box>
      ))}

      <TodoCancelDialog isOpen={isOpenCancelTodo} closeDialog={closeCancelTodo} cancel={cancelTodo} isSubmitting={isSubmitting} />
    </>
  )
}
